import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Sprout, ChevronDown, MessageSquare, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../../services/api';
import { businessToday } from '../../utils/time';
import PlanBidButton from '../PlanBidButton';
import StartPlanForm from '../StartPlanForm';
import PlanFinish from '../PlanFinish';
import CustomPlanBuilder from '../CustomPlanBuilder';

/**
 * Lawn care → Programs (was the Treatments page, Nov 2 2026: "revamp mowing
 * add-ons, combine with treatments" — one page, three tabs, nothing stored,
 * priced, scheduled or billed any differently).
 *
 * A PROGRAM is a plan (the Plan Cost Sheet, or a custom one) applied to one
 * yard. Each step becomes a round with a real job on the calendar, dated on
 * the day the truck already comes closest, riding that mow where there is one,
 * billed once a month on the plan. The crew logs what went down on the job page.
 *
 * One card per customer: who they are, what they are on, what they pay, the
 * next round, whether this month is booked, and the one thing that fixes what
 * is missing — then, opened, the rounds and the plan's settings. The old
 * every-4-weeks customers (services/planOverview calls them legacy) get the
 * same card, opened onto their schedule's price, applicator and cadence.
 */
const money = (n: any) => `$${Number(n || 0).toFixed(2)}`;
const day = (d?: string | null) => d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—';
// What went down this year before the app, on the old customers (owner, Sep 29 2026: "all the ferts and pres have not
// been done, only spray"). Moving one over records this, so the rest of the year is laid out from it.
const DONE_SO_FAR = 'Already done this year: weed control 1 (only the spray — no feed or pre-emergent yet).';
const OLD_KINDS = ['lawn_treatment', 'treatment', 'fertilizer', 'fertilizing'];

// ── Check plans (server/services/planAudit) ─────────────────────────────────
/**
 * Everything wrong with the plans, with the rows behind it, and one button that
 * fixes what can be fixed. Nothing is written until the button.
 */
export function PlanAudit() {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery('plan-audit', () => api.get('/treatments/audit').then(r => r.data));
  const fix = useMutation(() => api.post('/treatments/audit/apply').then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message, { autoClose: 12000 }); refetch(); qc.invalidateQueries('treatment-programs'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not fix the plans'); },
  });
  const answer = useMutation((v: { id: number; applied: boolean }) => api.post(`/treatments/visits/${v.id}/applied`, { applied: v.applied }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message); refetch(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save that'); },
  });
  if (isLoading || !data) return <p className="text-sm text-gray-500">Checking every plan…</p>;
  const c = data.counts || {};
  const fixable = c.done_before + c.not_applied + c.double_visits + c.priced_visits + c.old_schedules;
  const Section = ({ title, n, tone = 'amber', children }: { title: string; n: number; tone?: string; children: React.ReactNode }) => (
    <details className="card p-3" open={n > 0 && n <= 12}>
      <summary className="cursor-pointer text-sm font-semibold text-gray-900">
        <span className={n ? (tone === 'red' ? 'text-red-700' : 'text-amber-800') : 'text-emerald-700'}>{n ? `${n} · ` : '✓ '}</span>{title}
      </summary>
      {n > 0 && <div className="mt-2 text-xs text-gray-700 space-y-1 max-h-72 overflow-y-auto">{children}</div>}
    </details>
  );
  return (
    <div className="space-y-3">
      <div className="card p-4 space-y-2">
        <p className="text-sm text-gray-700">Every running lawn plan checked for double visits, double charges, and treatments marked done that never went down. Nothing changes until you press the button.</p>
        {fixable > 0 ? (
          <button onClick={() => { if (window.confirm('Fix everything listed as fixable below? Nothing is texted or charged.')) fix.mutate(); }} disabled={fix.isLoading}
            className="btn-primary text-sm">{fix.isLoading ? 'Fixing…' : `Fix these ${fixable}`}</button>
        ) : <p className="text-sm font-semibold text-emerald-700">Nothing to fix.</p>}
        {c.pay_to_void > 0 && <p className="text-xs text-amber-800">Fixing voids {money(c.pay_to_void)} of crew pay still owed for visits that were never put down.{c.pay_already_sent > 0 ? ` ${money(c.pay_already_sent)} was already paid out for those — that is left alone; settle it on the Pay page if you want it back.` : ''}</p>}
      </div>

      <Section title="Plan visits on finished mows — was it put down?" n={(data.waiting || []).length} tone="red">
        {(data.waiting || []).map((w: any) => (
          <div key={w.job_id} className="flex flex-wrap items-center gap-2 border-b border-gray-100 py-1">
            <span className="flex-1 min-w-[12rem]">{day(w.d)} · <b>{w.customer_name}</b> · {String(w.title || '').replace(/^Lawn plan: /, '')}</span>
            <button onClick={() => answer.mutate({ id: w.job_id, applied: true })} className="px-2 py-1 rounded border border-emerald-400 text-emerald-800 font-semibold">Yes, done</button>
            <button onClick={() => answer.mutate({ id: w.job_id, applied: false })} className="px-2 py-1 rounded border border-gray-300 text-gray-700">Not done</button>
          </div>
        ))}
      </Section>
      <Section title="Done-before count says fert and pre were done (only the spray was)" n={c.done_before}>
        {data.done_before.map((d: any) => <p key={d.program_id}>{d.customer_name} · {d.address} — {d.was} → <b>{d.becomes}</b></p>)}
      </Section>
      <Section title="Visits marked done with fert / pre that never went down" n={c.not_applied} tone="red">
        {data.not_applied.map((n: any) => (
          <p key={n.round_id}>{day(n.date)} · <b>{n.customer_name}</b> — takes off {n.off.join(', ')}{n.stays.length ? ` · keeps ${n.stays.join(', ')}` : ''} → {n.becomes}{n.pay_owed ? ` · voids ${money(n.pay_owed)} owed` : ''}{n.pay_sent ? ` · ${money(n.pay_sent)} already paid (left)` : ''}</p>
        ))}
      </Section>
      <Section title="Two visits in one month for the same plan" n={(data.double_visits || []).length} tone="red">
        {data.double_visits.map((d: any) => (
          <p key={d.round_id}>{d.month} · <b>{d.customer_name}</b> — {d.label} on {day(d.date)} (keeps {d.keeps.label} on {day(d.keeps.date)}) {d.fixable ? '→ removed' : <span className="text-red-700">— {d.why_not}</span>}</p>
        ))}
      </Section>
      <Section title="Plan visits priced on their own (billed twice with the monthly plan)" n={(data.priced_visits || []).length} tone="red">
        {data.priced_visits.map((p: any) => <p key={p.job_id}>{day(p.d)} · <b>{p.customer_name}</b> — {money(p.price)} {p.fixable ? '→ $0' : <span className="text-red-700">— already on a bill, correct it there</span>}</p>)}
      </Section>
      <Section title="Old per-visit treatment schedules still running on a plan yard" n={c.old_schedules} tone="red">
        {data.old_schedules.map((s: any) => <p key={s.schedule_id}><b>{s.customer_name}</b> · {s.address} — {s.service_type}, {s.future_visits} future visit{s.future_visits === 1 ? '' : 's'} → stopped</p>)}
      </Section>
      <Section title="Plan visits already billed for money (look at those bills)" n={c.invoiced_visits}>
        {data.invoiced_visits.map((v: any, i: number) => <p key={i}>{day(v.d)} · <b>{v.customer_name}</b> — {money(v.amount)} on {v.invoice_number} ({v.invoice_status})</p>)}
      </Section>
      <Section title="A plan month on two bills" n={c.double_billed}>
        {data.double_billed.map((b: any, i: number) => <p key={i}>Plan #{b.program_id} · {b.month} — on {b.n} bills</p>)}
      </Section>
      <Section title="Fertilizer add-ons on a mow for a yard already on a plan" n={c.feed_addons}>
        {data.feed_addons.map((f: any) => <p key={f.id}>{day(f.d)} · <b>{f.customer_name}</b> — {f.name} {money(f.price)}{f.invoiced ? ' (billed)' : ''}</p>)}
      </Section>
    </div>
  );
}

// ── One card per customer (server/services/planOverview + /treatments/programs) ──
/**
 * Owner, Oct 1 2026, on treatment plans: "it fully works". Plan customers and
 * the old every-4-weeks ones on one list: what they are on, what they pay, the
 * next visit (and whether it rides a mow), whether this month is booked, the
 * grass — and the one thing that fixes what is missing, right on the card.
 */
export default function ProgramsTab() {
  const qc = useQueryClient();
  const { data: ov, isLoading: ovLoading } = useQuery('treatment-overview', () => api.get('/treatments/overview').then(r => r.data));
  const { data, isLoading } = useQuery('treatment-programs', () => api.get('/treatments/programs').then(r => r.data));
  const { data: members } = useQuery('members-for-treatments', () => api.get('/crews/members/all').then(r => r.data.members as any[]));
  const [open, setOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'needs'>('all');
  // A stopped plan is history, not a second plan (owner, Sep 28 2026: "why does
  // Troller have 2 lawn plans" — one was cancelled). It stays out of the way.
  const [showStopped, setShowStopped] = useState(false);
  const [openProgram, setOpenProgram] = useState<number | null>(null);
  const refresh = () => { qc.invalidateQueries('treatment-overview'); qc.invalidateQueries('treatment-programs'); qc.invalidateQueries('treatment-program'); };
  if (isLoading || ovLoading) return <p className="text-sm text-gray-500">Reading every treatment customer…</p>;
  const programs: any[] = data?.programs || [];
  const plans: any[] = data?.plans || [];
  const rows: any[] = ov?.rows || [];
  const c = ov?.counts || {};
  const running = (p: any) => p.status === 'active' || p.status === 'paused';
  const lawn = programs.filter((p: any) => p.sheet_plan && running(p));
  const stopped = programs.filter((p: any) => p.sheet_plan && !running(p));
  const steps = programs.filter((p: any) => !p.sheet_plan);
  const byProgram = new Map<number, any>(programs.map((p: any) => [p.id, p]));
  const bySchedule = new Map<number, any>(plans.map((p: any) => [p.id, p]));
  // A treatment schedule the overview does not know (a bed-spray schedule of its own, say) still gets a card.
  const onACard = new Set(rows.filter(r => r.kind === 'legacy').map(r => Number(r.schedule_id)));
  const otherSchedules = plans.filter((p: any) => !onACard.has(Number(p.id)));
  const needs = rows.filter(r => r.fix).length;
  const shown = filter === 'needs' ? rows.filter(r => r.fix) : rows;
  if (!rows.length && !programs.length && !plans.length) return (
    <div className="card p-6 text-center text-sm text-gray-500">
      <Sprout className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
      Nobody is on a program yet. A plan bid that gets accepted lands here, or start one with the button above.
    </div>
  );
  return (<>
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
      <span><b className="text-gray-900">{rows.length}</b> on a program · {c.plans || 0} on a plan, {c.legacy || 0} on the old every-4-weeks job</span>
      {ov?.month_name && <span>· {ov.month_name}: {c.month_booked} of {c.month_due} booked</span>}
      {needs
        ? <button type="button" onClick={() => setFilter(f => (f === 'needs' ? 'all' : 'needs'))} className={`px-2 py-0.5 rounded-full border font-semibold ${filter === 'needs' ? 'bg-amber-100 border-amber-300 text-amber-900' : 'bg-white border-amber-300 text-amber-800'}`}>
            {needs} need{needs === 1 ? 's' : ''} something{filter === 'needs' ? ' · show all' : ''}
          </button>
        : <span className="text-emerald-700">· nothing missing</span>}
    </div>
    {lawn.some((p: any) => p.status === 'active') && <SeasonBar />}
    <LegacyBanner plans={plans} defaultApplicator={data?.default_applicator_id || null} />
    <div className="space-y-2">
      {shown.map(r => (
        <CustomerCard key={r.key} row={r} program={r.program_id ? byProgram.get(Number(r.program_id)) : null} schedule={r.schedule_id ? bySchedule.get(Number(r.schedule_id)) : null}
          members={members || []} defaultApplicator={data?.default_applicator_id || null}
          open={open === r.key} setOpen={() => setOpen(open === r.key ? null : r.key)} onChanged={refresh} />
      ))}
    </div>
    {otherSchedules.length > 0 && (
      <div className="mt-4">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Other treatment schedules · {otherSchedules.length}</p>
        <div className="card divide-y divide-gray-100">
          {otherSchedules.map((p: any) => <LegacyScheduleControls key={p.id} p={p} members={members || []} onChanged={refresh} />)}
        </div>
      </div>
    )}
    {stopped.length > 0 && (
      <button type="button" onClick={() => setShowStopped(v => !v)} className="mt-4 mb-2 text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1">
        Stopped plans · {stopped.length} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showStopped ? 'rotate-180' : ''}`} />
      </button>
    )}
    {stopped.length > 0 && showStopped && <ProgramList programs={stopped} open={openProgram} setOpen={setOpenProgram} />}
    {steps.length > 0 && <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mt-4 mb-2">Step programs</p>}
    {steps.length > 0 && <ProgramList programs={steps} open={openProgram} setOpen={setOpenProgram} />}

    {/* The housekeeping that used to be the Treatments page's other tabs — opened only when wanted, so nothing loads for nothing. */}
    <div className="mt-6 space-y-2">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">More</p>
      <Fold title="Check the plans" hint="double visits, double charges, treatments marked done that never went down"><PlanAudit /></Fold>
      <Fold title="Finish the old plans" hint="the every-4-weeks customers, year by year"><PlanFinish /></Fold>
      <Fold title="Crew offers" hint="one-off treatments the crew offered at the kerb"><OffersTab /></Fold>
    </div>
  </>);
}

/** A section that renders its contents only once opened. */
function Fold({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card">
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full text-left px-4 py-3 flex items-center gap-3">
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-gray-900">{title}</span>
          {hint && <span className="block text-xs text-gray-500">{hint}</span>}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

/**
 * One customer: the overview row (status, next round, price, the fix) and, opened, the rounds and settings — a plan's
 * ProgramDetail, or the old job's price / applicator / cadence controls.
 */
function CustomerCard({ row, program, schedule, members, defaultApplicator, open, setOpen, onChanged }: {
  row: any; program: any | null; schedule: any | null; members: any[]; defaultApplicator: number | null; open: boolean; setOpen: () => void; onChanged: () => void;
}) {
  const canOpen = (row.kind === 'plan' && row.program_id) || (row.kind === 'legacy' && schedule);
  return (
    <div className="card">
      <OverviewRow row={row} program={program} onChanged={onChanged} />
      {canOpen && (
        <button type="button" onClick={setOpen} className="w-full text-left px-3 pb-2 -mt-1 text-xs font-semibold text-brand-700 flex items-center gap-1">
          {row.kind === 'plan' ? (open ? 'Hide the rounds' : 'Rounds, timing and what is in the plan') : (open ? 'Hide the settings' : 'Price, applicator, how often')}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      )}
      {open && row.kind === 'plan' && row.program_id && <ProgramDetail id={Number(row.program_id)} />}
      {open && row.kind === 'legacy' && schedule && (
        <div className="border-t border-gray-100">
          {!defaultApplicator && !schedule.assigned_crew_member_id && <p className="px-4 pt-2 text-[11px] text-amber-700">No default applicator set (Settings → Treatment visits). Pick one here or set the default.</p>}
          <LegacyScheduleControls p={schedule} members={members} onChanged={onChanged} />
        </div>
      )}
    </div>
  );
}

function OverviewRow({ row, program = null, onChanged }: { row: any; program?: any | null; onChanged: () => void }) {
  const navigate = useNavigate();
  const [price, setPrice] = useState('');
  const ok = (d: any) => { toast.success(d?.message || 'Done', { autoClose: 8000 }); onChanged(); };
  const bad = (e: any) => { toast.error(e?.response?.data?.message || 'Could not do that'); };
  const book = useMutation(() => api.post(`/treatments/programs/${row.program_id}/book-month`, {}).then(r => r.data), { onSuccess: ok, onError: bad });
  const decide = useMutation((choice: string) => api.post(`/treatments/programs/${row.program_id}/mowing-stopped`, { choice }).then(r => r.data), { onSuccess: ok, onError: bad });
  const ontoMows = useMutation(() => api.post(`/treatments/programs/${row.program_id}/onto-mows`, {}).then(r => r.data), { onSuccess: ok, onError: bad });
  const savePrice = useMutation(() => api.put(`/treatments/schedules/${row.schedule_id}`, { price: Number(price) }).then(r => r.data), { onSuccess: ok, onError: bad });
  const stopOld = useMutation(() => api.post(`/treatments/schedules/${row.schedule_id}/stop-old`, {}).then(r => r.data), { onSuccess: ok, onError: bad });
  const fix = row.fix;
  const nextWords = row.next
    ? `${day(row.next.date)}${row.next.rides ? ', on the mow' : row.next.waits_for_mows ? ', on the mow once that month’s mows are on the calendar' : ', a visit of its own'}`
    : (row.status === 'paused' ? 'paused' : 'nothing booked');
  return (
    <div className="px-3 py-3 space-y-1">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <button type="button" onClick={() => navigate(`/customers/${row.customer_id}`)} className="text-sm font-semibold text-gray-900 hover:underline text-left break-words">{row.customer_name}</button>
          <p className="text-xs text-gray-600 break-words">
            {row.plan_name} · {row.grass || <span className="text-amber-800">grass not set</span>}
            {row.sqft ? ` · ${Number(row.sqft).toLocaleString()} sq ft` : ''}{row.kind === 'plan' && !row.mowing ? ' · we do not mow it' : ''}
            {program && program.rounds_total ? ` · ${program.rounds_done}/${program.rounds_total} rounds done` : ''}
          </p>
        </div>
        <span className="text-right whitespace-nowrap">
          <span className="block text-sm font-semibold text-gray-900">{row.price_words}</span>
          <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full ${row.status === 'paused' ? 'bg-amber-50 text-amber-800' : row.kind === 'legacy' ? 'bg-gray-100 text-gray-600' : 'bg-emerald-50 text-emerald-700'}`}>
            {row.status === 'paused' ? 'paused' : row.kind === 'legacy' ? 'old job' : 'on a plan'}
          </span>
        </span>
      </div>
      <p className="text-xs text-gray-700">
        Next: {nextWords} · {row.month.name}:{' '}
        {row.month.booked
          ? <span className="text-emerald-700">booked{row.month.date ? ` ${day(row.month.date)}` : ''}{row.month.done ? ', done' : ''}</span>
          : row.month.due ? <span className="font-semibold text-amber-800">not booked</span> : <span className="text-gray-500">nothing due</span>}
      </p>
      {fix && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2 text-xs text-amber-900 space-y-1.5">
          <p>{fix.say}</p>
          {fix.do === 'book_month' && (
            <button type="button" onClick={() => book.mutate()} disabled={book.isLoading} className="px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">{book.isLoading ? 'Booking…' : `Book ${row.month.name}`}</button>
          )}
          {fix.do === 'mow_stopped' && (
            <div className="flex flex-wrap gap-1.5">
              <button type="button" onClick={() => decide.mutate('keep')} disabled={decide.isLoading} className="px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-800 font-semibold disabled:opacity-50">Keep {money(fix.keep)}</button>
              {Number(fix.sheet_price) > 0 && <button type="button" onClick={() => decide.mutate('sheet')} disabled={decide.isLoading} className="px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">Move to {money(fix.sheet_price)}</button>}
              <button type="button" onClick={() => { if (window.confirm(`Stop ${row.customer_name}'s lawn plan? Upcoming visits come off the calendar; finished ones stay.`)) decide.mutate('stop'); }} disabled={decide.isLoading} className="px-2.5 py-1.5 rounded-lg border border-red-300 bg-white text-red-700 font-semibold disabled:opacity-50">Stop the plan</button>
            </div>
          )}
          {fix.do === 'onto_mows' && (
            <button type="button" onClick={() => ontoMows.mutate()} disabled={ontoMows.isLoading} className="px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">{ontoMows.isLoading ? 'Working…' : 'Put them on the mows'}</button>
          )}
          {fix.do === 'set_price' && (
            <div className="flex flex-wrap items-end gap-1.5">
              <label className="text-[11px] text-gray-700">What they pay a visit ($)
                <input type="number" inputMode="decimal" value={price} onChange={e => setPrice(e.target.value)} className="input mt-0.5 py-1 text-xs w-24" />
              </label>
              <button type="button" onClick={() => savePrice.mutate()} disabled={!(Number(price) > 0) || savePrice.isLoading} className="px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">Save</button>
            </div>
          )}
          {fix.do === 'stop_old' && (
            <button type="button" onClick={() => { if (window.confirm(`Stop ${row.customer_name}'s old every-4-weeks job? Its visits not done yet come off the calendar; the plan carries on.`)) stopOld.mutate(); }} disabled={stopOld.isLoading} className="px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">{stopOld.isLoading ? 'Stopping…' : 'Stop the old job'}</button>
          )}
          {(fix.do === 'set_grass' || fix.do === 'move') && <LegacyMove scheduleId={row.schedule_id} row={row} onDone={onChanged} />}
          {fix.do === 'applicator' && <button type="button" onClick={() => navigate('/settings#treatment-visits')} className="text-brand-700 font-semibold hover:underline">Open Settings</button>}
        </div>
      )}
    </div>
  );
}

/**
 * Move one old every-4-weeks customer onto the annual program (owner, Sep 19 2026: "the old treatment plan… four
 * fertilizers, two pre-emergents… finish off the year"). The grass his list of names suggests is picked for him but
 * saved only when he taps; the lawn size can be typed, or half the lot. It looks first, then moves.
 */
function LegacyMove({ scheduleId, row, onDone }: { scheduleId: number; row: any; onDone: () => void }) {
  const qc = useQueryClient();
  const suggested = row.grass_suggested?.grass || '';
  const [grass, setGrass] = useState<string>(row.grass_key || suggested);
  const [sqft, setSqft] = useState<string>(row.sqft ? String(row.sqft) : '');
  const done = (d: any) => { if (d) toast.success(d.message, { autoClose: 10000 }); qc.invalidateQueries('treatment-overview'); qc.invalidateQueries('treatment-programs'); onDone(); };
  const bad = (e: any) => { toast.error(e?.response?.data?.message || 'Could not do that'); };
  const saveGrass = useMutation(() => api.post('/treatments/bump/grass', { grasses: { [`s${scheduleId}`]: grass } }).then(r => r.data), { onSuccess: done, onError: bad });
  const move = useMutation(async () => {
    const body = { grass: row.grass_key || grass || undefined, sqft: Number(sqft) > 0 ? Number(sqft) : undefined };
    const look = await api.post(`/treatments/schedules/${scheduleId}/to-annual-plan`, { ...body, dry_run: true });
    if (!window.confirm(`${look.data.message}\n\n${DONE_SO_FAR}\n\nMove them?`)) return null;
    return (await api.post(`/treatments/schedules/${scheduleId}/to-annual-plan`, body)).data;
  }, { onSuccess: done, onError: bad });
  const why = row.grass_suggested?.matched_on ? `“${row.grass_suggested.matched_on}” is on your St. Augustine list` : 'not on your St. Augustine list';
  if (!row.grass_key) {
    return (
      <div className="flex flex-wrap items-end gap-1.5">
        <label className="text-[11px] text-gray-700">Grass
          <select value={grass} onChange={e => setGrass(e.target.value)} className="input mt-0.5 py-1 text-xs w-44">
            <option value="">Pick one…</option><option value="sa">St. Augustine</option><option value="bz">Bermuda / Zoysia</option>
          </select>
        </label>
        <button type="button" onClick={() => saveGrass.mutate()} disabled={!grass || saveGrass.isLoading} className="px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">{saveGrass.isLoading ? 'Saving…' : 'Set grass'}</button>
        {suggested && <span className="w-full text-[11px] text-gray-600">Picked for you: {row.grass_suggested.name} ({why}). Nothing is saved until you tap Set grass.</span>}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-end gap-1.5">
      <label className="text-[11px] text-gray-700">Lawn they pay for (sq ft)
        <input type="number" inputMode="numeric" value={sqft} onChange={e => setSqft(e.target.value)} placeholder={row.half_the_lot ? `half the lot is ${Number(row.half_the_lot).toLocaleString()}` : 'sq ft of grass'} className="input mt-0.5 py-1 text-xs w-40" />
      </label>
      {row.half_the_lot && String(row.half_the_lot) !== sqft && (
        <button type="button" onClick={() => setSqft(String(row.half_the_lot))} className="px-2 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700">Half the lot — {Number(row.half_the_lot).toLocaleString()}</button>
      )}
      <button type="button" onClick={() => move.mutate()} disabled={!(Number(sqft) > 0) || move.isLoading} className="px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">{move.isLoading ? 'Looking…' : 'Move to a plan'}</button>
    </div>
  );
}

// ── Lists of plans (stopped ones, step programs) ─────────────────────────────
function ProgramList({ programs, open, setOpen }: { programs: any[]; open: number | null; setOpen: (n: number | null) => void }) {
  return (
    <div className="card divide-y divide-gray-100">
      {programs.map((p: any) => (
        <div key={p.id}>
          <button onClick={() => setOpen(open === p.id ? null : p.id)} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50">
            <span className={`inline-block w-2 h-2 rounded-full ${p.status === 'active' ? 'bg-emerald-500' : p.status === 'cancelled' ? 'bg-gray-300' : 'bg-amber-400'}`} />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-gray-900 truncate">{p.customer_name} <span className="font-normal text-gray-500">· {p.name}</span></span>
              <span className="block text-xs text-gray-500 truncate">{p.address || 'no property'} · {p.rounds_done}/{p.rounds_total} rounds done{p.next_date ? ` · next ${day(p.next_date)}` : ''}</span>
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{p.status}</span>
            <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${open === p.id ? 'rotate-180' : ''}`} />
          </button>
          {open === p.id && <ProgramDetail id={p.id} />}
        </div>
      ))}
    </div>
  );
}

// ── The old every-4-weeks job: price, cadence, applicator, next visit ──────────
const label = (t: string) => String(t || '').replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
const okOr202 = (r: any) => r.status === 202;

/** His treatment customers were sold one thing: the annual program, billed monthly. The app had them as a bare every-4-weeks job. */
function LegacyBanner({ plans, defaultApplicator }: { plans: any[]; defaultApplicator: number | null }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries('treatment-programs');
  const allToAnnual = useMutation(async () => {
    const look = await api.post('/treatments/schedules/to-annual-plan-all', { dry_run: true });
    const d = look.data;
    const text = [`${d.message}.`, ...d.moved.map((m: string) => `• ${m}`), ...(d.refused.length ? ['', 'These need something first:', ...d.refused.map((m: string) => `• ${m}`)] : []), '', `${DONE_SO_FAR} (on each)`, '', 'Move them?'].join('\n');
    if (!d.moved.length) { window.alert(text.replace('\n\nMove them?', '')); return { data: { message: 'Nothing could be moved yet' }, status: 200 }; }
    if (!window.confirm(text)) return { data: { message: 'Left as they are' }, status: 200 };
    return api.post('/treatments/schedules/to-annual-plan-all', {});
  }, {
    onSuccess: (r: any) => { if (!okOr202(r)) toast.success(r.data?.message || 'Moved'); refresh(); qc.invalidateQueries('treatment-overview'); }, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not move them'); },
  });
  if (!plans.length) return null;
  return (<>
    {plans.some(p => OLD_KINDS.includes(String(p.service_type))) && (
      <div className="card p-3 flex flex-wrap items-center gap-2">
        <p className="flex-1 min-w-[12rem] text-xs text-gray-600">These are on the old "every 4 weeks" job, which does not know what goes down when. The <b>annual program</b> you sold is 4 fertilizer, 2 pre-emergent and 2 weed control a year, billed monthly. Moving them keeps their price, marks what you have already done this year, and books the rest on their mows.</p>
        <button onClick={() => allToAnnual.mutate()} disabled={allToAnnual.isLoading} className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">{allToAnnual.isLoading ? 'Looking…' : 'Move them all to the annual plan'}</button>
      </div>
    )}
    {!defaultApplicator && <p className="text-xs text-amber-700">No default applicator set (Settings → Treatment visits). Pick one per schedule or set the default.</p>}
  </>);
}

/** One old treatment schedule's controls (was a row of PlansOnSchedule). The schedule AND its upcoming visits change together. */
function LegacyScheduleControls({ p, members, onChanged }: { p: any; members: any[]; onChanged: () => void }) {
  const qc = useQueryClient();
  const refresh = () => { qc.invalidateQueries('treatment-programs'); onChanged(); };
  const update = useMutation(({ id, body }: { id: number; body: any }) => api.put(`/treatments/schedules/${id}`, body), {
    onSuccess: (r: any) => { if (!okOr202(r)) toast.success(r.data?.message || 'Updated'); refresh(); }, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not update'); },
  });
  const headsUp = useMutation((id: number) => api.post(`/treatments/schedules/${id}/heads-up`), {
    onSuccess: (r: any) => { if (!okOr202(r)) toast.success(r.data?.message || 'Texted'); }, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not text them'); },
  });
  // The old "lawn treatment every 4 weeks" job → the annual program he actually sold (4 fertilizer, 2 pre-emergent, 2 weed control, billed monthly).
  // It looks first and says exactly what it will do; nothing moves until he says yes.
  const toAnnual = useMutation(async () => {
    const look = await api.post(`/treatments/schedules/${p.id}/to-annual-plan`, { dry_run: true, grass: p.grass_key || undefined, sqft: p.sqft || undefined });
    if (!window.confirm(`${look.data.message}\n\n${DONE_SO_FAR}\n\nMove them?`)) return { data: { message: 'Left as it is' }, status: 200 };
    return api.post(`/treatments/schedules/${p.id}/to-annual-plan`, { grass: p.grass_key || undefined, sqft: p.sqft || undefined });
  }, {
    onSuccess: (r: any) => { if (!okOr202(r)) toast.success(r.data?.message || 'Moved'); refresh(); }, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not move them'); },
  });
  // A separate spray schedule → the Bed weed spray add-on riding that yard's mow (owner, Sep 19 2026). It asks first and says exactly what it will do.
  const toMow = useMutation(async () => {
    const look = await api.post(`/treatments/schedules/${p.id}/to-mow-addon`, { dry_run: true, cadence: 'month:1' });
    if (!window.confirm(`${look.data.message}\n\nDo it?`)) return { data: { message: 'Left as it is' }, status: 200 };
    return api.post(`/treatments/schedules/${p.id}/to-mow-addon`, { cadence: 'month:1' });
  }, {
    onSuccess: (r: any) => { if (!okOr202(r)) toast.success(r.data?.message || 'Done'); refresh(); }, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not move it onto the mow'); },
  });
  const stop = useMutation((id: number) => api.post(`/treatments/schedules/${id}/stop`), {
    onSuccess: (r: any) => { if (!okOr202(r)) toast.success(r.data?.message || 'Stopped'); refresh(); }, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not stop it'); },
  });
  return (
    <div className="px-4 py-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">{p.customer_name} <span className="font-normal text-gray-500">· {label(p.service_type)}</span></p>
          <p className="text-xs text-gray-500">{[p.address, p.city].filter(Boolean).join(', ')} · every {p.custom_interval_days || (p.frequency === 'biweekly' ? 14 : 7)} days{p.day_of_week != null ? ` on ${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][p.day_of_week]}s` : ''}</p>
          <p className="text-xs mt-0.5">
            <span className={p.next_date ? 'text-emerald-700' : 'text-gray-500'}>{p.next_date ? `Next ${day(p.next_date)}` : 'No visit booked'}</span>
            {p.last_done ? <span className="text-gray-500"> · last done {day(p.last_done)} · {p.done_count} so far</span> : null}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-gray-600">$
            <input type="number" inputMode="decimal" defaultValue={p.price ?? ''} placeholder="price" className={`input py-1 text-sm w-20 ml-1 ${p.price ? '' : 'border-amber-400'}`}
              onBlur={e => { const v = Number(e.target.value); if (v > 0 && v !== Number(p.price)) update.mutate({ id: p.id, body: { price: v } }); }} />
            <span className="text-gray-500"> /visit</span>
          </label>
          <select className="input py-1 text-sm" value={p.assigned_crew_member_id || ''} onChange={e => update.mutate({ id: p.id, body: { assigned_crew_member_id: e.target.value ? Number(e.target.value) : null } })}>
            <option value="">Applicator…</option>
            {members.map((m: any) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <select className="input py-1 text-sm" value={p.custom_interval_days || 28} onChange={e => update.mutate({ id: p.id, body: { custom_interval_days: Number(e.target.value) } })}>
            <option value={28}>every 4 weeks</option><option value={42}>every 6 weeks</option><option value={56}>every 8 weeks</option><option value={84}>every 12 weeks</option>
          </select>
          <button onClick={() => headsUp.mutate(p.id)} disabled={!p.next_date || headsUp.isLoading} className="text-xs text-brand-700 hover:underline disabled:opacity-40 inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" /> heads-up</button>
          {OLD_KINDS.includes(String(p.service_type)) && p.grass_key && p.sqft && (
            <button onClick={() => toAnnual.mutate()} disabled={toAnnual.isLoading} className="text-xs font-semibold text-emerald-700 hover:underline mr-2 disabled:opacity-50" title="The annual program you sold: 4 fertilizer, 2 pre-emergent, 2 weed control a year, billed monthly at this price">Move to the annual plan</button>
          )}
          {['beds_nutsedge_spray', 'bed_spray', 'weed_spray'].includes(String(p.service_type)) && (
            <button onClick={() => toMow.mutate()} disabled={toMow.isLoading} className="text-xs font-semibold text-emerald-700 hover:underline mr-2 disabled:opacity-50" title="Make it the Bed weed spray add-on on this yard's mow, once a month, and stop this separate schedule">Put it on the mow, monthly</button>
          )}
          <button onClick={() => { if (window.confirm(`Stop ${p.customer_name}'s ${label(p.service_type).toLowerCase()} plan? Upcoming visits come off the calendar.`)) stop.mutate(p.id); }} className="text-xs text-red-600 hover:underline">stop</button>
        </div>
      </div>
      {(!p.price || p.unpriced_visits > 0) && <p className="text-[11px] text-amber-700 mt-1">Needs a price — type it and the booked visits pick it up.</p>}
      {/* The grass and lawn size a move needs, right on the row (they had no field, so the move could never finish). */}
      {OLD_KINDS.includes(String(p.service_type)) && (!p.grass_key || !p.sqft) && (
        <div className="mt-2"><LegacyMove scheduleId={p.id} row={p} onDone={refresh} /></div>
      )}
      {!p.assigned_crew_member_id && <p className="text-[11px] text-amber-700 mt-0.5">No applicator — the crew won't see these visits until someone is picked.</p>}
    </div>
  );
}

/**
 * Timing for the whole program (owner, Sep 21 2026: "allow me to adjust timing
 * on treatment schedules better"). Where in the month the visits land, and
 * nudging the lot earlier or later. Visits done or started are never touched.
 */
function Timing({ program, onChanged }: { program: any; onChanged: () => void }) {
  const aim = String(program.aim_for || 'middle');
  const set = useMutation((body: any) => api.put(`/treatments/programs/${program.id}/timing`, body).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Timing changed', { autoClose: 7000 }); onChanged(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not change that timing'); },
  });
  const AIMS: [string, string][] = [['early', 'Early — the 5th'], ['middle', 'Middle — the 15th'], ['late', 'Late — the 25th']];
  return (
    <div className="mt-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Timing</p>
      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-gray-600">Aim for</span>
        {AIMS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => set.mutate({ aim_for: k, rebuild: true })} disabled={set.isLoading}
            className={`text-xs px-2 py-1 rounded-lg border font-semibold ${aim === k ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-gray-600 border-gray-300'}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-gray-600">Move everything</span>
        {[-14, -7, 7, 14].map(n => (
          <button key={n} type="button" onClick={() => set.mutate({ shift_days: n })} disabled={set.isLoading}
            className="text-xs px-2 py-1 rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold">
            {n > 0 ? `+${n}d` : `${n}d`}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-gray-500 mt-1.5">
        Each visit still rides the mow nearest that day. Visits done or started are never touched, and a date you move
        by hand stays put through any rebuild.
      </p>
    </div>
  );
}

function ProgramDetail({ id }: { id: number }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [changing, setChanging] = useState(false);
  const { data } = useQuery(['treatment-program', id], () => api.get(`/treatments/programs/${id}`).then(r => r.data.program));
  const refresh = () => { qc.invalidateQueries(['treatment-program', id]); qc.invalidateQueries('treatment-programs'); };
  const done = (r: any) => { if (r.status === 202) return true; return false; };
  const moveRound = useMutation(({ rid, date }: { rid: number; date: string }) => api.put(`/treatments/rounds/${rid}`, { scheduled_date: date }), {
    onSuccess: (r: any) => { if (!done(r)) toast.success('Moved'); refresh(); }, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not move it'); },
  });
  const unpin = useMutation((rid: number) => api.put(`/treatments/rounds/${rid}`, { pinned: false }), {
    onSuccess: (r: any) => { if (!done(r)) toast.success('Back on the plan’s own date at the next rebuild'); refresh(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not change that'); },
  });
  const skipRound = useMutation((rid: number) => api.put(`/treatments/rounds/${rid}`, { status: 'skipped' }), {
    onSuccess: (r: any) => { if (!done(r)) toast.success('Skipped'); refresh(); }, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not skip it'); },
  });
  const headsUp = useMutation((rid: number) => api.post(`/treatments/rounds/${rid}/heads-up`), {
    onSuccess: (r: any) => { if (!done(r)) toast.success(r.data?.message || 'Texted'); refresh(); }, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not text them'); },
  });
  const cancel = useMutation(() => api.post(`/treatments/programs/${id}/cancel`), {
    onSuccess: (r: any) => { if (!done(r)) toast.success(r.data?.message || 'Stopped'); refresh(); }, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not stop it'); },
  });
  if (!data) return <p className="px-4 pb-3 text-xs text-gray-500">Loading…</p>;
  const rounds: any[] = data.rounds || [];
  return (
    <div className="px-4 pb-4 bg-gray-50/60">
      {data.notes && <p className="text-xs text-gray-600 py-2">{data.notes}</p>}
      {data.season && <SeasonBlock program={data} onChanged={refresh} />}
      {data.sheet_plan && <Timing program={data} onChanged={refresh} />}
      {/* What is in the plan, changed on the same builder that made it: an extra added or dropped, a month moved (owner, Oct 22 2026). */}
      {data.sheet_plan && (data.status === 'active' || data.status === 'paused') && (
        changing
          ? <CustomPlanBuilder programId={id} customerId={data.customer_id} onDone={() => { setChanging(false); refresh(); }} onCancel={() => setChanging(false)} />
          : <button type="button" onClick={() => setChanging(true)} className="mt-2 text-xs font-semibold text-emerald-800 underline underline-offset-2">Change what is in the plan (extras, months, price)</button>
      )}
      {data.sheet_plan && <p className="text-[11px] text-gray-500 mt-2">Billed {data.monthly_price ? `${money(data.monthly_price)} ` : ''}once a month on the plan — the visits carry no price of their own.</p>}
      <table className="min-w-full text-xs mt-2">
        <thead><tr className="text-left text-gray-500"><th className="py-1 pr-3">Round</th><th className="py-1 pr-3">Day</th>{!data.sheet_plan && <th className="py-1 pr-3">Price</th>}<th className="py-1 pr-3">Status</th><th className="py-1"></th></tr></thead>
        <tbody>
          {rounds.map((r: any) => {
            const finished = r.status === 'done' || r.job_status === 'complete';
            const skipped = r.status === 'skipped' || r.job_status === 'cancelled';
            return (
              <tr key={r.id} className={`border-t border-gray-200 ${skipped ? 'opacity-50' : ''}`}>
                <td className="py-1.5 pr-3 font-medium text-gray-800">{r.sort_order + 1}. {r.label}{r.applications ? <span className="ml-1 text-emerald-700" title="Application logged">✓ logged</span> : null}</td>
                <td className="py-1.5 pr-3">
                  {finished || skipped ? day(r.scheduled_date || r.target_date)
                    : <>
                        <input type="date" className="input py-0.5 text-xs" defaultValue={String(r.scheduled_date || r.target_date || '').slice(0, 10)}
                          onBlur={e => { if (e.target.value && e.target.value !== String(r.scheduled_date || '').slice(0, 10)) moveRound.mutate({ rid: r.id, date: e.target.value }); }} />
                        {/* A date he set by hand stays put through any rebuild. */}
                        {Number(r.pinned) === 1 && (
                          <button onClick={() => unpin.mutate(r.id)} disabled={unpin.isLoading}
                            className="ml-1 text-[11px] text-emerald-700" title="You set this date, so nothing will move it. Tap to hand the month back to the plan.">
                            your date ✕
                          </button>
                        )}
                      </>}
                </td>
                {/* A plan visit is billed with the plan, monthly — it never had a price of its own. */}
                {!data.sheet_plan && <td className="py-1.5 pr-3">{money(r.price)}</td>}
                <td className="py-1.5 pr-3">{finished ? <span className="text-emerald-700">done</span> : skipped ? 'skipped' : (r.job_status || 'scheduled')}{r.heads_up_sent_at ? <span className="text-gray-500"> · texted</span> : null}</td>
                <td className="py-1.5 text-right whitespace-nowrap">
                  {r.job_id && <button onClick={() => navigate(`/jobs/${r.job_id}`)} className="text-brand-600 hover:underline mr-2">job</button>}
                  {!finished && !skipped && <>
                    <button onClick={() => headsUp.mutate(r.id)} disabled={headsUp.isLoading} className="text-brand-600 hover:underline mr-2 inline-flex items-center gap-0.5"><MessageSquare className="h-3 w-3" /> heads-up</button>
                    <button onClick={() => { if (window.confirm(`Skip "${r.label}"? Its visit comes off the calendar.`)) skipRound.mutate(r.id); }} className="text-gray-500 hover:underline">skip</button>
                  </>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {data.status === 'active' && (
        <div className="pt-3">
          <button onClick={() => { if (window.confirm('Stop this program? Upcoming visits come off the calendar; finished rounds stay.')) cancel.mutate(); }} disabled={cancel.isLoading}
            className="text-xs text-red-600 hover:underline">Stop the program</button>
        </div>
      )}
    </div>
  );
}

// ── Enrol a yard ─────────────────────────────────────────────────────────────
export function EnrollSheet({ onClose }: { onClose: () => void }) {
  const [search, setSearch] = useState('');
  const [customer, setCustomer] = useState<any | null>(null);
  const { data: found } = useQuery(['treat-cust-search', search], () => api.get('/customers', { params: { search } }).then(r => r.data.customers as any[]), { enabled: search.trim().length >= 2, keepPreviousData: true });
  const { data: options, isLoading } = useQuery(['customer-plan-options', customer?.id], () => api.get(`/customers/${customer.id}/plan-options`).then(r => r.data), { enabled: !!customer });
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 space-y-3 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-bold text-gray-900">Start a treatment program</h3>
            <p className="text-xs text-gray-500 mt-0.5">Send Estate, The Plan and Simple. They pick one and sign, like a bid.</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>
        {!customer ? (
          <div>
            {/* The results sit IN the flow, not floated over it. Absolutely
                positioned, they were clipped by this sheet's own scrollbox, so
                typing a name showed a list cut off at the edge (owner, Sep 21
                2026: "when i start a treatment program and put customer name in
                its cut off"). A long name wraps now instead of being chopped. */}
            <input className="input" placeholder="Search customers…" value={search} onChange={e => setSearch(e.target.value)} autoFocus />
            {(found || []).length > 0 && (
              <div className="mt-1 w-full bg-white border border-gray-200 rounded-md divide-y divide-gray-100 max-h-56 overflow-y-auto">
                {(found || []).map((c: any) => (
                  <button key={c.id} type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-brand-50 break-words"
                    onClick={() => { setCustomer(c); setSearch(''); }}>
                    {c.name}
                    {c.address ? <span className="block text-xs text-gray-500 break-words">{c.address}</span> : null}
                  </button>
                ))}
              </div>
            )}
            {search.trim().length >= 2 && (found || []).length === 0 && (
              <p className="mt-1 text-xs text-gray-500">Nobody by that name yet.</p>
            )}
          </div>
        ) : (
          <div className="flex items-start justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm">
            <span className="font-medium min-w-0 break-words">{customer.name}</span>
            <button className="text-xs text-brand-600 flex-shrink-0" onClick={() => setCustomer(null)}>Change</button>
          </div>
        )}
        {customer && isLoading && <p className="text-xs text-gray-500">Working out the prices for this yard…</p>}
        {customer && options && (
          <div className="space-y-3">
            {(options.yards || []).length === 0 && <p className="text-xs text-gray-600">No property on this customer yet — add the address first.</p>}
            {(options.yards || []).map((y: any) => (
              <div key={y.property_id} className="rounded-lg border border-emerald-200 p-3">
                <p className="text-sm font-semibold text-gray-900">{y.address || 'Yard'}</p>
                <p className="text-xs text-gray-600">
                  {y.turf_known ? <>Lawn <b>{Number(y.turf_sqft).toLocaleString()} sq ft</b></> : <>Lawn not measured — priced at {Number(y.priced_at_sqft).toLocaleString()} sq ft</>}
                  {y.grass ? <> · {y.grass}</> : <> · grass not set</>}
                </p>
                {y.on_plan && <p className="text-xs text-emerald-800 mt-1">Already on {y.on_plan.name}{y.on_plan.monthly ? ` at $${y.on_plan.monthly}/mo` : ''}.</p>}
                {!y.on_plan && (
                  <>
                    <div className="mt-2 space-y-1">
                      {y.plans.map((pl: any) => (
                        <p key={pl.plan} className="text-xs text-gray-800"><b>{pl.name}</b> — ${pl.monthly}/mo · {pl.visits} applications{pl.recommended ? <span className="text-emerald-700"> · recommended</span> : null}</p>
                      ))}
                    </div>
                    <PlanBidButton propertyId={y.property_id} customerId={customer.id} phone={options.customer?.phone || customer.phone} />
                    <StartPlanForm yard={y} customerId={customer.id} />
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── One-off offers the crew made at the kerb ─────────────────────────────────
export function OffersTab() {
  const qc = useQueryClient();
  const { data } = useQuery('treatment-offers', () => api.get('/treatments').then(r => r.data.suggestions as any[]));
  const refresh = () => qc.invalidateQueries('treatment-offers');
  const schedule = useMutation(({ id, date }: { id: number; date: string }) => api.post(`/treatments/${id}/schedule`, { scheduled_date: date }), {
    onSuccess: (r: any) => { if (r.status !== 202) toast.success(r.data?.message || 'Booked'); refresh(); }, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not book it'); },
  });
  const cancel = useMutation((id: number) => api.post(`/treatments/${id}/cancel`), { onSuccess: refresh, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not cancel'); } });
  const remove = useMutation((id: number) => api.delete(`/treatments/${id}`), { onSuccess: refresh, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not remove'); } });
  const rows = data || [];
  if (!rows.length) return <div className="card p-6 text-center text-sm text-gray-500">Nothing offered yet. The crew offers a treatment from a job page when they spot a problem; the customer answers on their own page.</div>;
  return (
    <div className="card divide-y divide-gray-100">
      {rows.map((t: any) => (
        <div key={t.id} className="px-4 py-3 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900">{t.customer_name} <span className="font-normal text-gray-500">· {t.treatment}</span></p>
            <p className="text-xs text-gray-500">{t.issue}{t.address ? ` · ${t.address}` : ''}{t.suggested_by ? ` · offered by ${t.suggested_by}` : ''} · {money(t.price)}</p>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${t.status === 'accepted' ? 'bg-emerald-50 text-emerald-700' : t.status === 'completed' ? 'bg-gray-100 text-gray-600' : t.status === 'declined' || t.status === 'cancelled' ? 'bg-gray-100 text-gray-500' : 'bg-amber-50 text-amber-700'}`}>{t.status}</span>
          <div className="text-xs whitespace-nowrap">
            {t.status === 'accepted' && <button onClick={() => { const d = window.prompt('Book it for which day? (YYYY-MM-DD)', businessToday()); if (d) schedule.mutate({ id: t.id, date: d }); }} className="text-brand-600 hover:underline mr-2">book</button>}
            {['offered', 'accepted'].includes(t.status) && <button onClick={() => cancel.mutate(t.id)} className="text-gray-500 hover:underline mr-2">cancel</button>}
            {t.status !== 'completed' && !t.created_job_id && <button onClick={() => { if (window.confirm('Remove this offer?')) remove.mutate(t.id); }} className="text-red-500 hover:underline">remove</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── The season so far ───────────────────────────────────────────────────────
// Owner, Sep 19 2026: "I've already pre-emerged once, fertilized twice and weed
// sprayed once for all of them — mark those done, schedule the rest on a mow,
// for right now just put it in the crew notes." What he did before the app is a
// count per plan (history only — no job, no pay, no bill); the booked visits
// are written into the crew notes of the mow they ride; and anything the sheet
// had in a month already gone is listed, never booked by itself.
const DONE_FIELDS: [string, string][] = [['pre', 'Pre-emergent'], ['fertilizer', 'Fertilizer'], ['post', 'Weed spray'], ['grub', 'Grub'], ['fungus', 'Fungus'], ['insect', 'Insect'], ['iron', 'Iron'], ['moisture', 'Moisture'], ['opener', 'Soil opener'], ['core', 'Core aeration']];

function DoneCounts({ value, onChange }: { value: Record<string, string>; onChange: (v: Record<string, string>) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {DONE_FIELDS.map(([k, label]) => (
        <label key={k} className="text-[11px] text-gray-600">{label}
          <input type="number" min="0" max="24" inputMode="numeric" value={value[k] ?? ''} onChange={e => onChange({ ...value, [k]: e.target.value })} className="input mt-0.5 w-16 py-1 text-xs" placeholder="0" />
        </label>
      ))}
    </div>
  );
}

function SeasonBar() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  // His count (Sep 29 2026): "all the ferts and pres have not been done, only spray".
  const [counts, setCounts] = useState<Record<string, string>>({ post: '1' });
  const refresh = () => { qc.invalidateQueries('treatment-programs'); qc.invalidateQueries('treatment-program'); };
  const ride = useMutation(() => api.post('/treatments/ride-mows').then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message); refresh(); }, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not do that'); },
  });
  const markAll = useMutation(() => api.put('/treatments/programs/all/done-before', { counts }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message); setOpen(false); refresh(); }, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save that'); },
  });
  return (
    <div className="card p-3 mb-2 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex-1 min-w-[12rem] text-xs text-gray-600">Plan visits ride the yard's mow that month and are written into <b>that mow's crew notes</b>, so the mowing crew reads them on the stop.</p>
        <button onClick={() => ride.mutate()} disabled={ride.isLoading} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 disabled:opacity-50">{ride.isLoading ? 'Working…' : 'Put the visits on the mows now'}</button>
        <button onClick={() => setOpen(v => !v)} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700">Done before the app…</button>
      </div>
      {open && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-2">
          <p className="text-xs text-gray-600">How many of each you had already done this season, on <b>every running plan</b>. History only: no job, no pay, no bill, no text. One plan can be set on its own by opening it.</p>
          <DoneCounts value={counts} onChange={setCounts} />
          <button onClick={() => { if (window.confirm('Write this count on every running plan? It replaces the count each plan has now.')) markAll.mutate(); }} disabled={markAll.isLoading} className="btn-primary text-xs py-1.5 px-3 disabled:opacity-50">{markAll.isLoading ? 'Saving…' : 'Mark these done on every plan'}</button>
        </div>
      )}
    </div>
  );
}

function SeasonBlock({ program, onChanged }: { program: any; onChanged: () => void }) {
  const s = program.season;
  const [edit, setEdit] = useState(false);
  let stored: Record<string, number> = {}; try { stored = program.done_before ? JSON.parse(program.done_before) : {}; } catch { stored = {}; }
  const [counts, setCounts] = useState<Record<string, string>>(Object.fromEntries(Object.entries(stored).map(([k, v]) => [k, String(v)])));
  const save = useMutation(() => api.put(`/treatments/programs/${program.id}/done-before`, { counts }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message); setEdit(false); onChanged(); }, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save that'); },
  });
  const catchUp = useMutation((keys: string[]) => api.post(`/treatments/programs/${program.id}/catch-up`, { keys }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message); onChanged(); }, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not add that'); },
  });
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 mt-2 text-xs text-gray-700 space-y-1">
      <p><b>{s.year} so far.</b> Done before the app: {s.done_before_words || 'nothing recorded'} <button onClick={() => setEdit(v => !v)} className="text-brand-600 hover:underline ml-1">{edit ? 'close' : 'change'}</button></p>
      {s.done_in_app_words && <p className="text-emerald-800">Done in the app: {s.done_in_app_words}.</p>}
      {edit && (
        <div className="py-1 space-y-2">
          <DoneCounts value={counts} onChange={setCounts} />
          <button onClick={() => save.mutate()} disabled={save.isLoading} className="btn-primary text-xs py-1 px-2.5 disabled:opacity-50">{save.isLoading ? 'Saving…' : 'Save for this plan'}</button>
        </div>
      )}
      <p>Still to come on the sheet: {s.left_words || 'nothing more this year'}.{s.catching_up_words ? <> Catching up on a coming visit: {s.catching_up_words}.</> : null}</p>
      {s.behind_list?.length > 0 && (
        <div>
          <p className="text-amber-800">Behind the sheet (its month has passed): {s.behind_words}. Nothing is booked for these by itself — tap one to put it on the next visit, or leave it.</p>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {s.behind_list.map((b: any, i: number) => (
              <button key={`${b.key}-${i}`} onClick={() => catchUp.mutate(b.keys && b.keys.length ? b.keys : [b.key])} disabled={catchUp.isLoading}
                className="px-2 py-1 rounded-full border border-amber-300 bg-amber-50 text-amber-900 disabled:opacity-50">+ {b.word} <span className="text-amber-700/70">({['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][b.month]})</span></button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
